package com.mgacreative.hotel.domain.repository

import com.mgacreative.hotel.core.network.SupabaseNetworkClient
import com.mgacreative.hotel.domain.model.PricePeriod
import io.github.jan.supabase.postgrest.postgrest
import kotlinx.datetime.*

interface PricingRepository {
    suspend fun getAllPricePeriods(): List<PricePeriod>
    suspend fun getPricePeriods(roomType: String): List<PricePeriod>
    suspend fun addPricePeriod(pricePeriod: PricePeriod): PricePeriod?
    suspend fun updatePricePeriod(id: String, name: String, start: String, end: String, price: Double): Boolean
    suspend fun deletePricePeriod(id: String): Boolean
    suspend fun calculateTotalPrice(roomTypeId: String, start: LocalDate, end: LocalDate, basePrice: Double): Double
}

class PricingRepositoryImpl(
    private val networkClient: SupabaseNetworkClient,
    private val tenantSession: com.mgacreative.hotel.core.tenant.TenantSession? = null
) : PricingRepository {

    override suspend fun getAllPricePeriods(): List<PricePeriod> {
        if (tenantSession?.isDemoMode == true) {
            return com.mgacreative.hotel.domain.demo.DemoDataProvider.pricePeriodsState.value
        }
        return try {
            networkClient.client.postgrest["price_periods"]
                .select()
                .decodeList<PricePeriod>()
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            emptyList()
        }
    }

    override suspend fun getPricePeriods(roomType: String): List<PricePeriod> {
        if (tenantSession?.isDemoMode == true) {
            return com.mgacreative.hotel.domain.demo.DemoDataProvider.pricePeriodsState.value.filter { it.roomType == roomType }
        }
        return try {
            networkClient.client.postgrest["price_periods"]
                .select {
                    filter {
                        eq("room_type", roomType)
                    }
                }
                .decodeList<PricePeriod>()
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            emptyList()
        }
    }

    override suspend fun addPricePeriod(pricePeriod: PricePeriod): PricePeriod? {
        return try {
            networkClient.client.postgrest["price_periods"]
                .insert(pricePeriod) {
                    select()
                }
                .decodeSingleOrNull<PricePeriod>()
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            null
        }
    }

    override suspend fun updatePricePeriod(id: String, name: String, start: String, end: String, price: Double): Boolean {
        return try {
            networkClient.client.postgrest["price_periods"]
                .update({
                    set("name", name)
                    set("start_date", start)
                    set("end_date", end)
                    set("price", price)
                }) {
                    filter {
                        eq("id", id)
                    }
                }
            true
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            false
        }
    }

    override suspend fun deletePricePeriod(id: String): Boolean {
        return try {
            networkClient.client.postgrest["price_periods"]
                .delete {
                    filter {
                        eq("id", id)
                    }
                }
            true
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            false
        }
    }

    // Calculates the total price for a given date range using periods (falling back to a base price if needed)
    override suspend fun calculateTotalPrice(roomTypeId: String, start: LocalDate, end: LocalDate, basePrice: Double): Double {
        val periods = getPricePeriods(roomTypeId)
        var total = 0.0
        var currentDate = start

        while (currentDate < end) {
            // Find if current date falls into any period
            // If multiple periods match, we take the one with the highest priority (e.g. latest created or highest price)
            // For now, we just pick the first matching period
            val matchingPeriod = periods.firstOrNull {
                val periodStart = LocalDate.parse(it.startDate)
                val periodEnd = LocalDate.parse(it.endDate)
                currentDate >= periodStart && currentDate <= periodEnd
            }

            total += matchingPeriod?.price ?: basePrice
            currentDate = currentDate.plus(1, DateTimeUnit.DAY)
        }

        return total
    }
}
